// src/components/admin/page-management/section-forms/BlogPostsForm.js
import React from 'react';
import { Info } from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';

const BlogPostsForm = ({ content, updateContent }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Blog Posts Section</h3>
      
      {/* Title Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Title Settings</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showTitle !== false}
              onChange={(e) => updateContent('showTitle', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Title</span>
          </label>
        </div>
        
        {content?.showTitle !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={content?.title || 'Latest From Our Blog'}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title Color</label>
              <ColorPicker
                color={content?.titleColor || '#1f2937'}
                onChange={(color) => updateContent('titleColor', color)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title Size</label>
                <select
                  value={content?.titleSize || 'text-3xl'}
                  onChange={(e) => updateContent('titleSize', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="text-2xl">Small</option>
                  <option value="text-3xl">Medium</option>
                  <option value="text-4xl">Large</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Title Alignment</label>
                <select
                  value={content?.titleAlignment || 'text-center'}
                  onChange={(e) => updateContent('titleAlignment', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="text-left">Left</option>
                  <option value="text-center">Center</option>
                  <option value="text-right">Right</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Title Font Weight</label>
                <select
                  value={content?.titleFontWeight || 'font-bold'}
                  onChange={(e) => updateContent('titleFontWeight', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="font-normal">Normal</option>
                  <option value="font-medium">Medium</option>
                  <option value="font-semibold">Semibold</option>
                  <option value="font-bold">Bold</option>
                </select>
              </div>
            </div>
          </>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Subtitle (Optional)</label>
          <textarea
            value={content?.subtitle || ''}
            onChange={(e) => updateContent('subtitle', e.target.value)}
            className="w-full p-2 border rounded"
            rows="2"
            placeholder="Enter a subtitle (leave empty to hide)"
          />
        </div>
        
        {content?.subtitle && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Subtitle Color</label>
            <ColorPicker
              color={content?.subtitleColor || '#4b5563'}
              onChange={(color) => updateContent('subtitleColor', color)}
            />
          </div>
        )}
      </div>
      
      {/* Content Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Content Settings</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Number of Posts to Display</label>
          <input
            type="number"
            min="1"
            max="12"
            value={content?.postsCount || 3}
            onChange={(e) => updateContent('postsCount', parseInt(e.target.value, 10))}
            className="w-32 p-2 border rounded"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Mobile Columns</label>
            <select
              value={content?.postsPerRow?.mobile || 1}
              onChange={(e) => updateContent('postsPerRow', {
                ...content?.postsPerRow,
                mobile: parseInt(e.target.value, 10)
              })}
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tablet Columns</label>
            <select
              value={content?.postsPerRow?.tablet || 2}
              onChange={(e) => updateContent('postsPerRow', {
                ...content?.postsPerRow,
                tablet: parseInt(e.target.value, 10)
              })}
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Desktop Columns</label>
            <select
              value={content?.postsPerRow?.desktop || 3}
              onChange={(e) => updateContent('postsPerRow', {
                ...content?.postsPerRow,
                desktop: parseInt(e.target.value, 10)
              })}
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
            </select>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Space Between Cards</label>
          <input
            type="text"
            value={content?.spaceBetweenCards || '2rem'}
            onChange={(e) => updateContent('spaceBetweenCards', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="e.g., 2rem, 32px"
          />
        </div>
      </div>
      
      {/* Card Style Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Card Style</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Card Style</label>
          <select
            value={content?.cardStyle || 'shadow'}
            onChange={(e) => updateContent('cardStyle', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="shadow">Shadow</option>
            <option value="outline">Outline</option>
            <option value="flat">Flat</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Card Border Radius</label>
          <input
            type="text"
            value={content?.cardBorderRadius || '0.5rem'}
            onChange={(e) => updateContent('cardBorderRadius', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="e.g., 0.5rem, 8px"
          />
        </div>
        
        {content?.cardStyle === 'shadow' && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Shadow Size</label>
            <select
              value={content?.cardShadowSize || 'shadow-xl'}
              onChange={(e) => updateContent('cardShadowSize', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="shadow">Small</option>
              <option value="shadow-md">Medium</option>
              <option value="shadow-lg">Large</option>
              <option value="shadow-xl">Extra Large</option>
            </select>
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Card Background Color</label>
          <ColorPicker
            color={content?.cardBgColor || '#ffffff'}
            onChange={(color) => updateContent('cardBgColor', color)}
          />
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.cardHoverEffect !== false}
              onChange={(e) => updateContent('cardHoverEffect', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Hover Effect</span>
          </label>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Featured Image Aspect Ratio</label>
          <input
            type="text"
            value={content?.imageAspectRatio || '60%'}
            onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="e.g., 60%, 50%, 75%"
          />
          <p className="text-xs text-gray-500 mt-1">
            Lower percentages make shorter images, higher percentages make taller images
          </p>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Placeholder Image URL</label>
          <input
            type="text"
            value={content?.placeholderImage || '/images/placeholder-blog.jpg'}
            onChange={(e) => updateContent('placeholderImage', e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>
      </div>
      
      {/* Post Content Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Post Content Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={content?.showCategory !== false}
                onChange={(e) => updateContent('showCategory', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">Show Category</span>
            </label>
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={content?.showDate !== false}
                onChange={(e) => updateContent('showDate', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">Show Date</span>
            </label>
          </div>
        </div>
        
        {content?.showCategory !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Category Text Color</label>
            <ColorPicker
              color={content?.categoryColor || '#2563eb'}
              onChange={(color) => updateContent('categoryColor', color)}
            />
          </div>
        )}
        
        {content?.showDate !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Date Format</label>
            <select
              value={content?.dateFormat || 'MM/DD/YYYY'}
              onChange={(e) => updateContent('dateFormat', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            </select>
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showExcerpt !== false}
              onChange={(e) => updateContent('showExcerpt', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Excerpt</span>
          </label>
        </div>
        
        {content?.showExcerpt !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Excerpt Length (lines)</label>
            <select
              value={content?.excerptLines || 2}
              onChange={(e) => updateContent('excerptLines', parseInt(e.target.value, 10))}
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Line</option>
              <option value={2}>2 Lines</option>
              <option value={3}>3 Lines</option>
              <option value={4}>4 Lines</option>
            </select>
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showReadMore !== false}
              onChange={(e) => updateContent('showReadMore', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show "Read More" Link</span>
          </label>
        </div>
        
        {content?.showReadMore !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">"Read More" Text</label>
              <input
                type="text"
                value={content?.readMoreText || 'Read More'}
                onChange={(e) => updateContent('readMoreText', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">"Read More" Color</label>
              <ColorPicker
                color={content?.readMoreColor || '#2563eb'}
                onChange={(color) => updateContent('readMoreColor', color)}
              />
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.readMoreIcon !== false}
                  onChange={(e) => updateContent('readMoreIcon', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Show Arrow Icon</span>
              </label>
            </div>
          </>
        )}
      </div>
      
      {/* "View All" Button Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">"View All" Button Settings</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showViewAllButton !== false}
              onChange={(e) => updateContent('showViewAllButton', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show "View All" Button</span>
          </label>
        </div>
        
        {content?.showViewAllButton !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Text</label>
              <input
                type="text"
                value={content?.viewAllButtonText || 'View All Posts'}
                onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Link</label>
              <input
                type="text"
                value={content?.viewAllButtonLink || '/blog'}
                onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Color</label>
                <ColorPicker
                  color={content?.viewAllButtonColor || '#033568'}
                  onChange={(color) => updateContent('viewAllButtonColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Text Color</label>
                <ColorPicker
                  color={content?.viewAllButtonTextColor || '#ffffff'}
                  onChange={(color) => updateContent('viewAllButtonTextColor', color)}
                />
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Spacing Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Spacing Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Margin Top</label>
            <input
              type="text"
              value={content?.marginTop || '2rem'}
              onChange={(e) => updateContent('marginTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 2rem, 32px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Margin Bottom</label>
            <input
              type="text"
              value={content?.marginBottom || '8rem'}
              onChange={(e) => updateContent('marginBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 8rem, 128px"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Padding Top</label>
            <input
              type="text"
              value={content?.paddingTop || '1rem'}
              onChange={(e) => updateContent('paddingTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 1rem, 16px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Padding Bottom</label>
            <input
              type="text"
              value={content?.paddingBottom || '1rem'}
              onChange={(e) => updateContent('paddingBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 1rem, 16px"
            />
          </div>
        </div>
      </div>
      
      {/* Animation Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Animation Settings</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.animationEnabled !== false}
              onChange={(e) => updateContent('animationEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Animations</span>
          </label>
        </div>
        
        {content?.animationEnabled !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Animation Delay Between Cards (ms)</label>
            <input
              type="number"
              min="0"
              max="500"
              step="50"
              value={content?.animationDelay || 150}
              onChange={(e) => updateContent('animationDelay', parseInt(e.target.value, 10))}
              className="w-32 p-2 border rounded"
            />
          </div>
        )}
      </div>
      
      {/* API Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">API Settings</h4>
          <div className="text-blue-500">
            <Info size={16} />
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          These settings are for advanced users. Most users should leave these at their default values.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">API Path</label>
          <input
            type="text"
            value={content?.apiPath || '/api/blog/posts?limit=6&sort=newest&status=published'}
            onChange={(e) => updateContent('apiPath', e.target.value)}
            className="w-full p-2 border rounded"
          />
          <p className="text-xs text-gray-500 mt-1">
            You can customize the query parameters to control which posts are fetched.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BlogPostsForm;